// components/LocationCard.tsx
import React from "react";
import { Card, CardContent, Typography, Box, Chip } from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";

export interface Location {
  locationId: string;
  lat: number;
  lng: number;
  name: string;
  activeOffers: number;
  city: string;
  googleMapsLink: string;
  distance: number;
  tags: string[];
  storeHours: {
    [day: string]: {
      open: string;
      close: string;
    };
  };
}

interface LocationCardProps {
  location: Location;
}

const LocationCard: React.FC<LocationCardProps> = ({ location }) => {
  const handleNavigate = () => {
    window.open(location.googleMapsLink, "_blank", "noopener,noreferrer");
  };

  // Helper to check if the location is open based on current day and time.
  const isOpen = () => {
    const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
    const todayHours = location.storeHours[today];

    if (!todayHours) return false;

    const { open, close } = todayHours;

    // If both open and close times are "00:00", treat as closed
    if (open === "00:00" && close === "00:00") return false;

    const [openHour, openMinute] = open.split(":").map(Number);
    const [closeHour, closeMinute] = close.split(":").map(Number);
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const openMinutes = openHour * 60 + openMinute;
    const closeMinutes = closeHour * 60 + closeMinute;

    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };

  const openStatus = isOpen();

  const goToLocationPage = () => {
    window.location.href = "/nearme/" + location.locationId;
  };

  return (
    <Card
      onClick={() => goToLocationPage()}
      elevation={4}
      sx={{
        maxWidth: 400,
        width: "100%",
        margin: "0 auto",
        background: "linear-gradient(135deg, #283e51, #4b79a1)",
        borderRadius: 2,
        color: "white",
        mb: 2,
        position: "relative", // for absolute positioning of chip and navigation box
      }}
    >
      {/* Chip to display open/closed status */}
      <Chip
        label={openStatus ? "Open" : "Closed"}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          height: 22,
          fontSize: "0.7rem",
          fontWeight: "medium",
          px: 1,
          minWidth: 80,
          backgroundColor: openStatus ? "success.main" : "#8B0000", // dark red
          color: "white",
        }}
      />
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {location.name}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Offers: {location.activeOffers}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          City: {location.city}
        </Typography>
      </CardContent>
      <Box
        onClick={handleNavigate}
        sx={{
          position: "absolute",
          bottom: 8,
          right: 8,
          backgroundColor: "rgba(0,0,0,0.5)",
          padding: "4px 8px",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <NearMeIcon sx={{ fontSize: 20, color: "white", mr: 0.5 }} />
        <Typography
          variant="caption"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          {location.distance < 10
            ? location.distance.toFixed(2)
            : location.distance.toFixed(1)}{" "}
          km
        </Typography>
      </Box>
    </Card>
  );
};

export default LocationCard;
